import { useCallback } from 'react';
import { type TableSelectedCell, useTableRows } from '@/components/custom/erp/input-table-v2';

export interface DetailRow {
  uuid: string;
  line?: number;
  dien_giai?: string;
  ma_loai_hd?: string;
  [key: string]: MuaHangRecord;
}

export type SelectedCellInfo = TableSelectedCell;

const ensureRowDefaults = <T extends DetailRow>(row: T, initialDienGiai?: string): T => ({
  ...row,
  ma_loai_hd: row.ma_loai_hd ?? '0',
  dien_giai: row.dien_giai ?? initialDienGiai ?? ''
});

const normalizeInitialRows = <T extends DetailRow>(rows: T[] = [], initialDienGiai?: string): T[] => {
  if (rows && rows.length > 0) {
    return rows.map((row, index) =>
      ensureRowDefaults(
        {
          ...row,
          uuid: row.uuid && row.uuid !== '' ? row.uuid : `temp-${Date.now()}-${index}`
        } as T,
        initialDienGiai
      )
    );
  }

  return [
    ensureRowDefaults(
      {
        uuid: `temp-${Date.now()}`,
        line: 1
      } as T,
      initialDienGiai
    )
  ];
};

export function useDetailRows(initialRows: DetailRow[] = [], initialDienGiai?: string) {
  const normalizedRows = normalizeInitialRows(initialRows, initialDienGiai);

  const {
    rows,
    setRows,
    selectedRowUuid,
    selectedRow,
    selectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange,
    handleAddRow: baseHandleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    setNonWritableFields,
    setPasteConfigs,
    isPasting
  } = useTableRows<DetailRow>({
    initialRows: normalizedRows,
    onFieldValueChange: (row, field, value) =>
      ({
        ...(field === 'dien_giai' ? { dien_giai: value } : {}),
        ...(field === 'ma_loai_hd' ? { ma_loai_hd: value } : {})
      }) as Partial<DetailRow>,
    defaultField: 'dien_giai'
  });

  const handleAddRow = () => {
    baseHandleAddRow();
    setRows(prev => {
      if (!prev.length) return prev;
      const updated = [...prev];
      const lastIndex = updated.length - 1;
      updated[lastIndex] = ensureRowDefaults(updated[lastIndex], initialDienGiai);
      return updated;
    });
  };

  const updateDienGiaiForEmptyRows = useCallback(
    (newDienGiai: string) => {
      setRows(prevRows => prevRows.map(row => (row.dien_giai ? row : { ...row, dien_giai: newDienGiai })));
    },
    [setRows]
  );

  return {
    rows,
    setRows,
    selectedRowUuid,
    selectedRow,
    selectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    setNonWritableFields,
    setPasteConfigs,
    updateDienGiaiForEmptyRows,
    isPasting
  };
}
